import { chronicles, ERAS } from "./chronicles";

export interface Achievement {
  id: string;
  name: string;
  description: string;
  category: "region" | "era" | "global";
  icon: string;
  chronicleIds: string[];
  /**
   * Completionist achievements require not only that every chronicle
   * in `chronicleIds` be COMPLETED, but also that every memory flag
   * (every ending, every secret, every unlockable note) for those
   * chronicles be discovered. The check happens in
   * `useAchievements.isUnlocked` by reading the chronicle-memory
   * store; absence of this flag means the standard "completed every
   * chronicle" rule applies.
   */
  requireAllFlags?: boolean;
}

// Achievements track only currently-playable chronicles. Without this filter,
// achievements for regions whose chronicles are still in development (HRE,
// Italy, Scandinavia, Eastern Europe, Balkans, Pan-European) would be
// permanently unreachable — they'd show 0/N forever with N counting
// chronicles the player can't actually complete. As more chronicles ship,
// they automatically become part of their region/era achievement.
const playable = chronicles.filter((c) => c.available);

// Regional achievements
const regionalAchievements: Achievement[] = [
  {
    id: "region-lowCountries",
    name: "Lion of the Low Countries",
    description: "Complete all chronicles set in the Low Countries.",
    category: "region",
    icon: "🦁",
    chronicleIds: playable.filter(c => c.regionId === "lowCountries").map(c => c.id),
  },
  {
    id: "region-britishIsles",
    name: "Rule Britannia!",
    description: "Complete all chronicles set in Britain and Ireland.",
    category: "region",
    icon: "👑",
    chronicleIds: playable.filter(c => c.regionId === "britishIsles").map(c => c.id),
  },
  {
    id: "region-hre",
    name: "Holy Roman Emperor",
    description: "Complete all chronicles set in Central Europe.",
    category: "region",
    icon: "🦅",
    chronicleIds: playable.filter(c => c.regionId === "hre").map(c => c.id),
  },
  {
    id: "region-iberia",
    name: "Conquistador of Iberia",
    description: "Complete all chronicles set in the Iberian Peninsula.",
    category: "region",
    icon: "⚔️",
    chronicleIds: playable.filter(c => c.regionId === "iberia").map(c => c.id),
  },
  {
    id: "region-france",
    name: "Sun King's Legacy",
    description: "Complete all chronicles set in France.",
    category: "region",
    icon: "⚜️",
    chronicleIds: playable.filter(c => c.regionId === "france").map(c => c.id),
  },
  {
    id: "region-italia",
    name: "Renaissance Master",
    description: "Complete all chronicles set in Italy.",
    category: "region",
    icon: "🏛️",
    chronicleIds: playable.filter(c => c.regionId === "italia").map(c => c.id),
  },
  {
    id: "region-scandinavia",
    name: "Saga of the North",
    description: "Complete all chronicles set in the Nordics.",
    category: "region",
    icon: "ᚱ",
    chronicleIds: playable.filter(c => c.regionId === "scandinavia").map(c => c.id),
  },
  {
    id: "region-easternEurope",
    name: "Eastern Frontier",
    description: "Complete all chronicles set in Eastern Europe.",
    category: "region",
    icon: "🐻",
    chronicleIds: playable.filter(c => c.regionId === "easternEurope").map(c => c.id),
  },
  {
    id: "region-balkans",
    name: "Crossroads of Empires",
    description: "Complete all chronicles set in the Balkans.",
    category: "region",
    icon: "🏰",
    chronicleIds: playable.filter(c => c.regionId === "balkans").map(c => c.id),
  },
];

// Era achievements — custom names matching timeline eras
const ERA_ACHIEVEMENT_META: Record<string, { name: string; icon: string }> = {
  "Early Medieval":      { name: "After Rome Fell",              icon: "🏚️" },
  "High Medieval":       { name: "Age of Kingdoms",              icon: "🏰" },
  "Late Medieval":       { name: "Age of Upheaval",              icon: "⚔️" },
  "Early Modern":        { name: "Age of Empires",               icon: "🌍" },
  "Age of Revolutions":  { name: "The World Turned Upside Down", icon: "🔥" },
  "Industrial & National": { name: "Forging Nations",            icon: "⚒️" },
  "Age of World Wars":   { name: "Total War",                    icon: "💣" },
  "Cold War":            { name: "The Divided Continent",        icon: "🧊" },
};

// One era achievement per ERA, ALWAYS. Every era currently has shipped
// chronicles; if an era ever had none, AchievementShowcase.tsx filters out
// zero-chronicle achievements so no "Coming Soon" placeholder is shown (the
// rest of the engine treats a total === 0 achievement as permanently locked —
// isUnlocked returns false).
const eraAchievements: Achievement[] = ERAS.map(era => {
  const eraChronicles = playable.filter(
    c => c.dateRange[0] >= era.start && c.dateRange[0] < era.end
  );
  const meta = ERA_ACHIEVEMENT_META[era.label] || { name: `Chronicler of the ${era.label}`, icon: "📜" };
  return {
    id: `era-${era.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
    name: meta.name,
    description: `Complete all chronicles from the ${era.label} era (${era.start}–${era.end}).`,
    category: "era" as const,
    icon: meta.icon,
    chronicleIds: eraChronicles.map(c => c.id),
  };
});

// Pan-European achievement (grouped with regional)
const panEuropeanAchievement: Achievement = {
  id: "pan-european",
  name: "The Fate of a Continent",
  description: "Complete all Pan-European chronicles that shaped the entire continent.",
  category: "region",
  icon: "🌐",
  chronicleIds: playable.filter(c => c.regionId === "panEuropean").map(c => c.id),
};

// Global achievement
const globalAchievement: Achievement = {
  id: "global-grand-chronicler",
  name: "Grand Chronicler of Europe",
  description: "You have lived through every chronicle currently available across the continent.",
  category: "global",
  icon: "🧭",
  chronicleIds: playable.map(c => c.id),
};

// ── Completionist tier ─────────────────────────────────────────
//
// Sits one rung above the standard region / era / global achievements:
// requires the player to have not only completed every chronicle in
// scope but also discovered every memory flag those chronicles
// expose (every ending, every secret, every historical encounter).
// When a regional completionist unlocks, the region's map shield
// upgrades from gilded to full-gold + subtle shine.
//
// Per-region naming uses the "{region} Completionist" pattern the
// player asked for; the global tier echoes "True Chronicler" as a
// step beyond "Grand Chronicler of Europe" so the showcase has a
// clear topmost prize.
const COMPLETIONIST_REGION_META: Record<string, { name: string; icon: string }> = {
  lowCountries:   { name: "Low Countries Completionist",   icon: "🦁" },
  britishIsles:   { name: "British Isles Completionist",   icon: "👑" },
  hre:            { name: "Central Europe Completionist",  icon: "🦅" },
  iberia:         { name: "Iberian Completionist",         icon: "⚔️" },
  france:         { name: "France Completionist",          icon: "⚜️" },
  italia:         { name: "Italy Completionist",           icon: "🏛️" },
  scandinavia:    { name: "Nordics Completionist",         icon: "ᚱ" },
  easternEurope:  { name: "Eastern Europe Completionist",  icon: "🐻" },
  balkans:        { name: "Balkans Completionist",         icon: "🏰" },
  panEuropean:    { name: "Pan-European Completionist",    icon: "🌐" },
};

const completionistRegionalAchievements: Achievement[] = Object.entries(COMPLETIONIST_REGION_META).map(([regionId, meta]) => ({
  id: `completionist-region-${regionId}`,
  name: meta.name,
  description: `Discover every ending, every secret, and every memory flag across all chronicles set in ${meta.name.replace(" Completionist", "")}.`,
  category: "region" as const,
  icon: meta.icon,
  chronicleIds: playable.filter(c => c.regionId === regionId).map(c => c.id),
  requireAllFlags: true,
}));

const completionistGlobalAchievement: Achievement = {
  id: "completionist-global-true-chronicler",
  name: "True Chronicler of Europe",
  description: "Nothing left undiscovered: every chronicle, every ending, every secret, every memory flag across the continent.",
  category: "global",
  icon: "🦉",
  chronicleIds: playable.map(c => c.id),
  requireAllFlags: true,
};

// Empty achievements (regions / eras with no currently-playable chronicles)
// are still generated here, but the showcase filters them out so no
// "Coming Soon" placeholders are shown — every region and era has shipped
// chronicles, and the game presents all content as playable.
export const achievements: Achievement[] = [
  ...regionalAchievements,
  ...eraAchievements,
  panEuropeanAchievement,
  globalAchievement,
  ...completionistRegionalAchievements,
  completionistGlobalAchievement,
];
